import React, { useEffect, useState } from "react";
import { Spin } from "antd";
import { useNavigate } from "react-router-dom";
import dayjs from "dayjs";
import { httpService } from "../../../../services/httpService.service";
import { getTaskManagerHome } from "../../../../services/features/dashboard.service";
import { getProjectIPCompleteness } from "../../../../services/features/ipPlan.service";
import { isActiveTask } from "../../../../services/helpFunctions/taskUrgency";
import { RolesEnum } from "../../../../services/rolesEnum";
import { isAuthorized } from "../../../../services/authService";
import { HOME_COLORS } from "../homeTheme";

// Project Hub — the at-a-glance snapshot + quick navigation that replaces the
// Team Leaderboard on the home screen. It reuses the very same endpoints the
// sibling home panels use (site visits, red flags, IP completeness, tasks) so
// there's no new backend surface, just one consolidated overview.
const OPEN_FLAG_STATUSES = [1, 2, 3];

// Quick-nav destinations, role-gated the same way SidebarMenu / QuickLinksPanel
// are, so the hub never offers a page the user can't open.
const TILES = [
  {
    label: "IP Planning",
    path: "/project/planning?ip-planning",
    roles: [RolesEnum.ProjectManager, RolesEnum.SystemAdmin, RolesEnum.Guest],
  },
  {
    label: "Visits",
    path: "/project/planning?visits",
    roles: [
      RolesEnum.ProjectManager,
      RolesEnum.SystemAdmin,
      RolesEnum.FieldCoordinator,
      RolesEnum.FieldEnumerator,
      RolesEnum.FieldLogistics,
      RolesEnum.DataAnalyst,
      RolesEnum.Guest,
      RolesEnum.LimitedGuest,
    ],
  },
  {
    label: "Tasks",
    path: "/project/planning?tasks",
    roles: [
      RolesEnum.ProjectManager,
      RolesEnum.SystemAdmin,
      RolesEnum.FieldCoordinator,
      RolesEnum.FieldLogistics,
      RolesEnum.DataAnalyst,
      RolesEnum.FieldTeamLeader,
      RolesEnum.Guest,
    ],
  },
  {
    label: "Implementation",
    path: "/project/implementation?WaitingForTracking",
    roles: [
      RolesEnum.ProjectManager,
      RolesEnum.SystemAdmin,
      RolesEnum.FieldCoordinator,
      RolesEnum.FieldTeamLeader,
      RolesEnum.FieldEnumerator,
      RolesEnum.FieldLogistics,
      RolesEnum.DataAnalyst,
      RolesEnum.Guest,
    ],
  },
  {
    label: "Reporting",
    path: "/project/reporting?FieldTeamStatusBoard",
    roles: [
      RolesEnum.ProjectManager,
      RolesEnum.SystemAdmin,
      RolesEnum.FieldCoordinator,
      RolesEnum.FieldTeamLeader,
      RolesEnum.FieldEnumerator,
      RolesEnum.FieldLogistics,
      RolesEnum.DataAnalyst,
    ],
  },
  {
    label: "Completed",
    path: "/project/completed?ReportingList",
    roles: [
      RolesEnum.ProjectManager,
      RolesEnum.SystemAdmin,
      RolesEnum.FieldCoordinator,
      RolesEnum.Guest,
      RolesEnum.LimitedGuest,
    ],
  },
  {
    label: "Flags",
    path: "/project/flags?Flags",
    roles: [
      RolesEnum.ProjectManager,
      RolesEnum.SystemAdmin,
      RolesEnum.FieldCoordinator,
      RolesEnum.FieldTeamLeader,
      RolesEnum.FieldEnumerator,
      RolesEnum.FieldLogistics,
      RolesEnum.DataAnalyst,
    ],
  },
  {
    label: "IP Management",
    path: "/project/ip-management",
    roles: [
      RolesEnum.ProjectManager,
      RolesEnum.SystemAdmin,
      RolesEnum.FieldCoordinator,
      RolesEnum.FieldTeamLeader,
      RolesEnum.FieldEnumerator,
      RolesEnum.FieldLogistics,
      RolesEnum.DataAnalyst,
      RolesEnum.Guest,
      RolesEnum.LimitedGuest,
    ],
  },
  {
    label: "Calendar",
    path: "/project/calendar",
    roles: [
      RolesEnum.ProjectManager,
      RolesEnum.SystemAdmin,
      RolesEnum.FieldCoordinator,
      RolesEnum.FieldTeamLeader,
      RolesEnum.FieldEnumerator,
      RolesEnum.FieldLogistics,
      RolesEnum.DataAnalyst,
    ],
  },
  {
    label: "AI Assistant",
    path: "/project/ai",
    roles: [
      RolesEnum.ProjectManager,
      RolesEnum.SystemAdmin,
      RolesEnum.FieldCoordinator,
    ],
  },
  {
    label: "Audit Trail",
    path: "/project/audit",
    roles: [RolesEnum.ProjectManager, RolesEnum.SystemAdmin],
  },
];

const ProjectHubPanel = () => {
  const navigate = useNavigate();
  const projectID = localStorage.getItem("currentProjectID");
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({
    visits: 0,
    stalledVisits: 0,
    openFlags: 0,
    ipAvg: null,
    tasksDueThisWeek: 0,
    stalledTasks: 0,
  });

  useEffect(() => {
    if (!projectID) {
      setLoading(false);
      return;
    }
    setLoading(true);

    const getP = (url) =>
      new Promise((resolve) =>
        httpService.get(url, (res) => resolve(res?.data || []), () => resolve([]))
      );

    const statusParams = [2, 3, 4, 5, 6]
      .map((s) => `SiteVisitStatus=${s}`)
      .join("&");

    const visitsP = getP(
      `/api/Site/getAllSiteVisits?ProjectId=${projectID}&IgnorePagination=true&${statusParams}`
    );
    const flagsP = getP(
      `/api/RedFlag/getAllRedFlags?ProjectId=${projectID}&IgnorePagination=true`
    );
    const ipP = new Promise((resolve) =>
      getProjectIPCompleteness(
        projectID,
        (res) => resolve(res?.data || res || []),
        () => resolve([])
      )
    );
    const tasksP = new Promise((resolve) =>
      getTaskManagerHome(
        projectID,
        (res) => resolve(res?.data || res || []),
        () => resolve([])
      )
    );

    Promise.all([visitsP, flagsP, ipP, tasksP]).then(
      ([visits, flags, ip, taskItems]) => {
        const visitList = Array.isArray(visits) ? visits : [];
        const flagList = Array.isArray(flags) ? flags : [];
        const ipList = Array.isArray(ip) ? ip : [];
        const tasks = (Array.isArray(taskItems) ? taskItems : []).map(
          (it) => it?.task || it
        );

        const openFlags = flagList.filter((f) =>
          OPEN_FLAG_STATUSES.includes(Number(f?.flagStatus ?? f?.status))
        ).length;

        const ipAvg =
          ipList.length > 0
            ? Math.round(
                ipList.reduce((sum, i) => sum + (i.percentage || 0), 0) /
                  ipList.length
              )
            : null;

        const weekEnd = dayjs().add(7, "day").endOf("day");
        const startOfToday = dayjs().startOf("day");
        const tasksDueThisWeek = tasks.filter((t) => {
          if (!t?.deadline || !isActiveTask(t)) return false;
          const d = dayjs(t.deadline);
          return d.isAfter(startOfToday) && d.isBefore(weekEnd);
        }).length;

        setStats({
          visits: visitList.length,
          stalledVisits: visitList.filter((v) => v?.isStalled).length,
          openFlags,
          ipAvg,
          tasksDueThisWeek,
          stalledTasks: tasks.filter((t) => t?.isStalled).length,
        });
        setLoading(false);
      }
    );
  }, [projectID]);

  if (loading) {
    return (
      <div className="homePanel">
        <div className="homePanelLoading">
          <Spin />
        </div>
      </div>
    );
  }

  const stalledTotal = stats.stalledVisits + stats.stalledTasks;

  const KPIS = [
    {
      key: "visits",
      value: stats.visits,
      label: "Site visits",
      color: HOME_COLORS.brand,
      path: "/project/planning?visits",
    },
    {
      key: "flags",
      value: stats.openFlags,
      label: "Open flags",
      color: HOME_COLORS.alert,
      path: "/project/flags?Flags",
    },
    {
      key: "ip",
      value: stats.ipAvg == null ? "—" : `${stats.ipAvg}%`,
      label: "Avg IP completeness",
      color: HOME_COLORS.ok,
      path: "/project/planning?ip-planning",
    },
    {
      key: "due",
      value: stats.tasksDueThisWeek,
      label: "Tasks due this week",
      color: HOME_COLORS.warn,
      path: "/project/planning?tasks",
    },
    {
      key: "stalled",
      value: stalledTotal,
      label: "Stalled items",
      color: HOME_COLORS.stalled,
      path: "/project/planning?tasks",
    },
  ];

  const tiles = TILES.filter((t) => isAuthorized(t.roles));

  return (
    <div className="homePanel">
      <div className="homePanelHead">
        <h3>Project hub</h3>
        <span className="homePanelCount">At a glance</span>
      </div>

      <div className="homeHubKpis">
        {KPIS.map((k) => (
          <div
            key={k.key}
            className="homeHubKpi"
            onClick={() => navigate(k.path)}
            role="button"
          >
            <span className="homeHubKpiValue">{k.value}</span>
            <span className="homeHubKpiLabel">
              <span className="homeDot" style={{ background: k.color }} />
              {k.label}
            </span>
          </div>
        ))}
      </div>

      <div className="homeHubNav">
        {tiles.map((t) => (
          <button
            key={t.path}
            className="homeHubTile"
            onClick={() => navigate(t.path)}
          >
            {t.label}
          </button>
        ))}
      </div>
    </div>
  );
};

export default ProjectHubPanel;
